<!-- Button -->
<button [matMenuTriggerFor]="userActions" mat-icon-button>
    <span class="relative">
        <img *ngIf="showAvatar && user?.avatar" [src]="user?.avatar" class="w-7 h-7 rounded-full" />
        <mat-icon *ngIf="!showAvatar || !user?.avatar" [svgIcon]="'heroicons_outline:user-circle'"></mat-icon>
        <span
            class="absolute right-0 bottom-0 w-2 h-2 rounded-full"
            [ngClass]="{
                'mr-px mb-px': !showAvatar || !user?.avatar,
                'bg-green-500': user?.status === 'online',
                'bg-amber-500': user?.status === 'away',
                'bg-red-500': user?.status === 'busy',
                'bg-gray-400': user?.status === 'not-visible',
            }"
        ></span>
        <!-- Multi-session badge -->
        <span
            *ngIf="otherSessions.length > 0"
            class="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full bg-primary text-on-primary text-[10px] font-bold leading-none"
        >
            {{ sessions.length }}
        </span>
    </span>
</button>

<mat-menu #userActions="matMenu" [xPosition]="'before'" class="min-w-72">
    <!-- Active account info -->
    <button (click)="navigateToProfile()" mat-menu-item>
        <span class="flex flex-col leading-none">
            <span>{{ "Signed in as:" | transloco }}</span>
            <span class="mt-1.5 text-md font-semibold">{{ user?.email }}</span>
            <span *ngIf="user?.organization?.name" class="mt-1 text-xs text-hint">{{ user?.organization?.name }}</span>
        </span>
    </button>

    <mat-divider class="my-1"></mat-divider>

    <!-- Other sessions (multi-session) -->
    <ng-container *ngIf="otherSessions.length > 0">
        <div class="px-4 py-1.5">
            <span class="text-xs font-medium text-hint uppercase tracking-wider">{{ "Other accounts" | transloco }}</span>
        </div>

        <button
            *ngFor="let session of otherSessions"
            mat-menu-item
            class="h-auto py-2"
            (click)="switchAccount(session)"
            [disabled]="switchingTo !== null"
        >
            <div class="flex items-center gap-3 w-full">
                <div class="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300">
                    {{ getInitials(session.name) }}
                </div>
                <div class="flex flex-col min-w-0 flex-1">
                    <span class="text-sm truncate leading-tight">{{ session.name }}</span>
                    <span class="text-xs text-secondary truncate leading-tight">{{ session.email }}</span>
                    <span *ngIf="session.organizationName" class="text-xs text-hint truncate leading-tight">{{ session.organizationName }}</span>
                </div>
                <div class="flex-shrink-0">
                    <mat-spinner
                        *ngIf="switchingTo === session.userId"
                        [diameter]="18"
                    ></mat-spinner>
                    <button
                        *ngIf="switchingTo !== session.userId"
                        mat-icon-button
                        class="w-6 h-6 min-h-0 min-w-0"
                        [matTooltip]="'Remove account' | transloco"
                        (click)="removeAccount(session, $event)"
                    >
                        <mat-icon class="text-hint icon-size-4" [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
                    </button>
                </div>
            </div>
        </button>

        <mat-divider class="my-1"></mat-divider>
    </ng-container>

    <!-- Add another account -->
    <button mat-menu-item (click)="addAccount()">
        <mat-icon [svgIcon]="'heroicons_outline:plus-circle'"></mat-icon>
        <span>{{ "Add another account" | transloco }}</span>
    </button>

    <mat-divider class="my-1"></mat-divider>

    <!-- Vista de cliente (D-SET32): solo super admin, y solo en pantallas que la soportan. -->
    <ng-container *ngIf="clientViewAvailable">
        <div class="px-4 py-1.5">
            <span class="text-xs font-medium text-hint uppercase tracking-wider">{{ "clientView.menu_title" | transloco }}</span>
        </div>
        <button mat-menu-item data-testid="client-view-internal" (click)="setClientView(false)">
            <mat-icon [svgIcon]="'heroicons_outline:shield-check'"></mat-icon>
            <span class="flex-1">{{ "clientView.internal" | transloco }}</span>
            <mat-icon *ngIf="!clientViewOn" class="icon-size-4 text-primary" [svgIcon]="'heroicons_outline:check'"></mat-icon>
        </button>
        <button mat-menu-item data-testid="client-view-client" (click)="setClientView(true)">
            <mat-icon [svgIcon]="'heroicons_outline:eye'"></mat-icon>
            <span class="flex-1">{{ "clientView.client" | transloco }}</span>
            <mat-icon *ngIf="clientViewOn" class="icon-size-4 text-primary" [svgIcon]="'heroicons_outline:check'"></mat-icon>
        </button>
        <mat-divider class="my-1"></mat-divider>
    </ng-container>

    <!-- Navigation -->
    <span class="flex flex-col gap-2 leading-none">
        <button (click)="navigateToProfileSettings()" mat-menu-item>
            <span>{{ "Profile Settings" | transloco }}</span>
        </button>
        <button (click)="navigateToHelpCenter()" mat-menu-item>
            <span>{{ "Help Center" | transloco }}</span>
        </button>
    </span>

    <mat-divider class="my-2"></mat-divider>

    <!-- Sign out -->
    <button (click)="signOut()" mat-menu-item>
        <mat-icon [svgIcon]="'heroicons_outline:arrow-right-on-rectangle'"></mat-icon>
        <span>{{ "Sign out" | transloco }}</span>
    </button>
    <button *ngIf="otherSessions.length > 0" (click)="signOutAll()" mat-menu-item>
        <mat-icon [svgIcon]="'heroicons_outline:arrow-right-on-rectangle'" class="text-warn"></mat-icon>
        <span class="text-warn">{{ "Sign out of all accounts" | transloco }}</span>
    </button>
</mat-menu>

<!-- Hidden status menu (kept for backward compatibility) -->
<mat-menu #userStatus="matMenu" class="user-status-menu">
    <button (click)="updateUserStatus('online')" mat-menu-item>
        <span class="w-4 h-4 mr-3 rounded-full bg-green-500"></span>
        <span>Online</span>
    </button>
    <button (click)="updateUserStatus('away')" mat-menu-item>
        <span class="w-4 h-4 mr-3 rounded-full bg-amber-500"></span>
        <span>Away</span>
    </button>
    <button (click)="updateUserStatus('busy')" mat-menu-item>
        <span class="w-4 h-4 mr-3 rounded-full bg-red-500"></span>
        <span>Busy</span>
    </button>
    <button (click)="updateUserStatus('not-visible')" mat-menu-item>
        <span class="w-4 h-4 mr-3 rounded-full bg-gray-400"></span>
        <span>Invisible</span>
    </button>
</mat-menu>
